import React, { useEffect, useRef, useState } from "react";
import { Search, X, ChevronDown } from "lucide-react";
import axios from "axios";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format, isValid } from "date-fns";
import { EVENTOS_INTERNOS, normalizarTipoEvento } from "../utils/eventosInternos";
import { es } from "date-fns/locale";
import { useAuth } from "../context/AuthContext";
import {
  HiPlus,
  HiTrash,
  HiPencilSquare,
  HiUserGroup,
  HiXMark,
  HiWrenchScrewdriver,
  HiExclamationTriangle,
} from "react-icons/hi2";

function SearchableTecnicoSelect({ tecnicos, value, onChange, placeholder }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const selected = tecnicos.find((t) => t.id === value);
  const filtered = search.trim()
    ? tecnicos.filter((t) => t.nombre_tecnico?.toLowerCase().includes(search.toLowerCase()))
    : tecnicos;
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  return (
    <div ref={containerRef} className="relative w-full">
      <button
        type="button"
        onClick={() => { setOpen((p) => !p); setTimeout(() => inputRef.current?.focus(), 0); }}
        className="flex w-full items-center justify-between rounded-xl border border-zinc-300 bg-white px-3 py-2.5 text-left text-sm text-zinc-800 shadow-sm outline-none transition focus:border-zinc-900"
      >
        {selected ? <span className="truncate">{selected.nombre_tecnico}</span> : <span className="text-zinc-400">{placeholder || "Seleccionar..."}</span>}
        <span className="flex shrink-0 items-center gap-1 text-zinc-400">
          {selected && <X size={14} className="cursor-pointer hover:text-rose-500" onClick={(e) => { e.stopPropagation(); onChange(""); }} />}
          <ChevronDown size={14} className={`transition ${open ? "rotate-180" : ""}`} />
        </span>
      </button>
      {open && (
        <div className="absolute z-[60] mt-1 w-full overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-xl">
          <div className="flex items-center gap-2 border-b border-zinc-200 px-3 py-2">
            <Search size={14} className="shrink-0 text-zinc-400" />
            <input ref={inputRef} type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar técnico..." className="w-full bg-transparent text-sm outline-none" />
            {search && <X size={14} className="shrink-0 cursor-pointer text-zinc-400 hover:text-rose-500" onClick={() => setSearch("")} />}
          </div>
          <ul className="max-h-56 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <li className="px-3 py-3 text-center text-xs text-zinc-400">Sin resultados</li>
            ) : (
              filtered.map((t) => (
                <li key={t.id} onClick={() => { onChange(t.id); setSearch(""); setOpen(false); }}
                  className={`flex cursor-pointer items-center gap-2 px-3 py-2 text-sm transition hover:bg-zinc-50 ${t.id === value ? "font-semibold text-zinc-900" : "text-zinc-700"}`}>
                  <span className="truncate">{t.nombre_tecnico}</span>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

const PanelControlInfo = () => {
  const [tecnicos, setTecnicos] = useState([]);
  const [maquinas, setMaquinas] = useState([]);
  const [newMaquina, setNewMaquina] = useState("");
  const [vacaciones, setVacaciones] = useState([]);
  const [selectedTecnico, setSelectedTecnico] = useState("");
  const [fechaInicio, setFechaInicio] = useState(null);
  const [fechaFin, setFechaFin] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user } = useAuth();
  const [modalLoteOpen, setModalLoteOpen] = useState(false);
  const [modalVacacionesOpen, setModalVacacionesOpen] = useState(false);
  const [selectedTecnicos, setSelectedTecnicos] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [selectedTecnicoModal, setSelectedTecnicoModal] = useState("");
  const [selectedTipoAsueto, setSelectedTipoAsueto] = useState("");
  const [comentarios, setComentarios] = useState("");
  const [vacacionesModal, setVacacionesModal] = useState([]);

  const handleModalVacacionesOpen = (tecnico) => {
    setSelectedTecnicoModal(tecnico);
    fetchVacacionesById(tecnico.id);
    setModalVacacionesOpen(true);
  };

  const handleChangeMultiple = (tecnicoId) => {
    setSelectedTecnicos((prev) => {
      const next = prev.includes(tecnicoId) ? prev.filter((id) => id !== tecnicoId) : [...prev, tecnicoId];
      setSelectAll(next.length === tecnicos.length);
      return next;
    });
  };

  const handleToggleSelectAll = () => {
    if (selectAll) {
      setSelectedTecnicos([]);
    } else {
      setSelectedTecnicos(tecnicos.map((t) => t.id));
    }
    setSelectAll(!selectAll);
  };

  /* ====================== MAQUINAS ====================== */
  const fetchMaquinas = async () => {
    try {
      const response = await axios.get("https://desarrollotecnologicoar.com/api2/maquinas");
      setMaquinas(response.data);
    } catch (error) {
      console.error("Error fetching machines:", error);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`https://desarrollotecnologicoar.com/api2/maquinas/${id}`);
      fetchMaquinas();
    } catch (error) {
      console.error("Error deleting machine:", error);
    }
  };

  const agregarMaquina = async () => {
    try {
      await axios.post("https://desarrollotecnologicoar.com/api2/maquinas", { maquina: newMaquina });
      fetchMaquinas();
      setNewMaquina("");
    } catch (err) {
      console.error("Error al agregar el técnico:", err);
    }
  };

  /* ====================== VACACIONES ====================== */
  const fetchTecnicos = async () => {
    try {
      const response = await axios.get("https://desarrollotecnologicoar.com/api5/tecnicos");
      let data = response.data;
      data.sort((a, b) => a.nombre_tecnico.localeCompare(b.nombre_tecnico));
      setTecnicos(data);
    } catch (error) {
      console.error("Error fatal info tecnicos:", error);
    }
  };

  const fetchVacaciones = async () => {
    try {
      const response = await axios.get("https://desarrollotecnologicoar.com/api5/vacaciones");
      setVacaciones(response.data);
    } catch (error) {
      console.error("Error fatal info vacaciones:", error);
    }
  };

  const formatFecha = (fecha) => {
    const dia = new Date(`${String(fecha).slice(0, 10)}T00:00:00`);
    return isValid(dia) ? format(dia, "EEEE d 'de' MMMM 'del' yyyy", { locale: es }) : "Sin fecha";
  };

  useEffect(() => {
    fetchMaquinas();
    fetchTecnicos();
    fetchVacaciones();
  }, []);

  const fetchVacacionesById = async (id) => {
    try {
      const response = await axios.get(`https://desarrollotecnologicoar.com/api5/vacaciones/${id}`);
      setVacacionesModal(response.data);
    } catch (error) {
      console.error("Error fatal info vacaciones:", error);
    }
  };

  const submittingRef = useRef(false);

  const guardarEventos = async (esLote = false) => {
    if (submittingRef.current) return;
    const ids = esLote ? [...selectedTecnicos] : [selectedTecnico].filter(Boolean);
    if (ids.length < (esLote ? 2 : 1) || !EVENTOS_INTERNOS[selectedTipoAsueto] ||
        !isValid(fechaInicio) || !isValid(fechaFin) || fechaInicio > fechaFin) {
      alert("Selecciona el tipo, las fechas válidas y " + (esLote ? "al menos dos técnicos." : "un técnico."));
      return;
    }
    submittingRef.current = true;
    setIsSubmitting(true);
    try {
      await axios.post("https://desarrollotecnologicoar.com/api5/eventos_internos", {
        tecnicos: ids,
        tipo_asueto: selectedTipoAsueto,
        fecha_inicio: format(fechaInicio, "yyyy-MM-dd"),
        fecha_fin: format(fechaFin, "yyyy-MM-dd"),
        comentarios,
        nombre_agente: user?.displayName || "",
        correo_agente: user?.email || "",
      });
      setSelectedTecnico("");
      setSelectedTecnicos([]);
      setSelectAll(false);
      setFechaInicio(null);
      setFechaFin(null);
      setSelectedTipoAsueto("");
      setComentarios("");
      setModalLoteOpen(false);
      await fetchVacaciones();
      alert(`${ids.length} registro(s) guardado(s) correctamente.`);
    } catch (error) {
      alert(error.response?.data?.error || "No se pudo confirmar el guardado. Revisa el listado antes de reintentar.");
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const handleDeleteVacaciones = async (id_vacacion) => {
    try {
      await axios.delete(`https://desarrollotecnologicoar.com/api5/eventos_internos/${id_vacacion}`);
      fetchVacaciones();
      if (selectedTecnicoModal?.id) fetchVacacionesById(selectedTecnicoModal.id);
      alert("Registro eliminado con éxito");
    } catch (error) {
      console.error("Error al eliminar vacaciones:", error);
      alert(error.response?.data?.error || "Error al eliminar el registro");
    }
  };

  /* ================================================================== */
  return (
    <div className="page-container min-h-screen bg-gradient-to-b from-zinc-50 via-white to-zinc-100 rounded-3xl">
      <div className="mb-8 rounded-3xl border border-zinc-200 bg-white/95 shadow-sm p-6 lg:p-8">
        <div className="inline-flex items-center rounded-full bg-zinc-900 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white mb-3">
          Control Center
        </div>
        <h1 className="page-title text-zinc-900 mb-2">Panel de Control de Información</h1>
        <p className="text-sm text-zinc-500">Módulo centralizado para gestionar registros de información</p>

        <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="rounded-2xl border border-zinc-200 bg-zinc-50 px-4 py-3">
            <p className="text-xs text-zinc-500 uppercase tracking-wide">Máquinas</p>
            <p className="text-xl font-bold text-zinc-900">{maquinas.length}</p>
          </div>
          <div className="rounded-2xl border border-zinc-200 bg-zinc-50 px-4 py-3">
            <p className="text-xs text-zinc-500 uppercase tracking-wide">Técnicos</p>
            <p className="text-xl font-bold text-zinc-900">{tecnicos.length}</p>
          </div>
          <div className="rounded-2xl border border-zinc-200 bg-zinc-50 px-4 py-3">
            <p className="text-xs text-zinc-500 uppercase tracking-wide">Registros</p>
            <p className="text-xl font-bold text-zinc-900">{vacaciones.length}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6">
        {/* ====================== LEFT: Maquinas (DEPRECATED - hidden) ====================== */}
        {false && <div className="space-y-4 opacity-50 pointer-events-none select-none">
          {/* Add machine */}
          <div className="card border-zinc-200 bg-white rounded-2xl p-5 relative">
            <span className="absolute top-3 right-3 text-[10px] font-bold uppercase tracking-widest bg-zinc-200 text-zinc-500 px-2 py-0.5 rounded-full">Deprecated</span>
            <h2 className="section-title mb-3 text-zinc-900">Agregar nueva máquina</h2>
            <input
              className="input w-full mb-3 border-zinc-300 focus:border-zinc-900 focus:ring-zinc-900/10"
              placeholder="Nombre de la máquina"
              value={newMaquina}
              onChange={(e) => setNewMaquina(e.target.value)}
            />
            <button className="btn-primary w-full flex items-center justify-center gap-2 bg-zinc-900 hover:bg-black" onClick={agregarMaquina}>
              <HiPlus className="h-4 w-4" />
              Agregar Máquina
            </button>
          </div>

          {/* Machine list */}
          <div className="card border-zinc-200 bg-white rounded-2xl p-5 max-h-[500px] overflow-y-auto">
            <h2 className="section-title mb-3 text-zinc-900">Lista de Máquinas</h2>
            <div className="divide-y divide-zinc-200">
              {maquinas.map((maquina) => (
                <div key={maquina.id} className="flex items-center justify-between py-2.5">
                  <span className="text-sm text-zinc-700">&#10148; {maquina.maquina}</span>
                  <div className="flex items-center gap-1">
                    <button className="btn-icon text-red-500 hover:bg-red-50" onClick={() => handleDelete(maquina.id)}>
                      <HiTrash className="h-4 w-4" />
                    </button>
                    <button className="btn-icon text-zinc-700 hover:bg-zinc-100">
                      <HiPencilSquare className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>}

        {/* ====================== RIGHT: Vacaciones ====================== */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Vacation form - 60% */}
          <div className="lg:col-span-3">
          <div className="card border-zinc-200 bg-white rounded-2xl p-5 relative">
            <button
              className="absolute top-3 right-3 btn-icon text-zinc-700 hover:bg-zinc-100"
              onClick={() => setModalLoteOpen(true)}
              title="Asignar por lote"
            >
              <HiUserGroup className="h-5 w-5" />
            </button>

            <h2 className="section-title mb-3 text-zinc-900">
              Gestión de vacaciones, trabajo interno y días feriados
            </h2>

            <fieldset disabled={isSubmitting} className="space-y-3">
              <div>
                <label className="label text-zinc-700">Técnico para el registro</label>
                <SearchableTecnicoSelect
                  tecnicos={tecnicos}
                  value={selectedTecnico}
                  onChange={(id) => setSelectedTecnico(id ? Number(id) : "")}
                  placeholder="Buscar técnico..."
                />
              </div>

              <div>
                <label className="label text-zinc-700">Tipo de registro</label>
                <select className="input w-full border-zinc-300 focus:border-zinc-900 focus:ring-zinc-900/10" value={selectedTipoAsueto} onChange={(e) => setSelectedTipoAsueto(e.target.value)}>
                  <option value="">Seleccionar tipo</option>
                  {Object.keys(EVENTOS_INTERNOS).map(tipo => <option key={tipo} value={tipo}>{tipo}</option>)}
                </select>
              </div>

              <div>
                <label className="label text-zinc-700">Comentarios adicionales (opcional)</label>
                <textarea
                  className="input w-full border-zinc-300 focus:border-zinc-900 focus:ring-zinc-900/10"
                  rows={3}
                  maxLength={1000}
                  value={comentarios}
                  onChange={(e) => setComentarios(e.target.value)}
                />
              </div>

              <div className="flex gap-3">
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
                  <div className="flex-1">
                    <DatePicker
                      label="Fecha inicio"
                      value={fechaInicio}
                      format="dd/MM/yyyy"
                      onChange={(value) => setFechaInicio(value)}
                      slotProps={{ textField: { size: "small", fullWidth: true } }}
                    />
                  </div>
                  <div className="flex-1">
                    <DatePicker
                      label="Fecha fin"
                      value={fechaFin}
                      format="dd/MM/yyyy"
                      onChange={(value) => setFechaFin(value)}
                      slotProps={{ textField: { size: "small", fullWidth: true } }}
                    />
                  </div>
                </LocalizationProvider>
              </div>

              <button
                className="btn-primary w-full flex items-center justify-center gap-2 bg-zinc-900 hover:bg-black"
                onClick={() => guardarEventos()}
                disabled={isSubmitting}
              >
                <HiWrenchScrewdriver className="h-4 w-4" />
                {isSubmitting ? "Registrando..." : "Agregar Registro"}
              </button>
            </fieldset>
          </div>
          </div>

          {/* Technician vacation list - 40% */}
          <div className="lg:col-span-2">
          <div className="card border-zinc-200 bg-white rounded-2xl p-5 max-h-[700px] overflow-y-auto">
            <h2 className="section-title mb-3 text-zinc-900">Listado de técnicos (últimos registros)</h2>
            <div className="space-y-2">
              {tecnicos.map((tecnico) => {
                const vacacion = vacaciones.find((v) => v.id_tecnico === tecnico.id);
                return (
                  <div
                    key={tecnico.id}
                    onClick={() => handleModalVacacionesOpen(tecnico)}
                    className="p-3 rounded-xl border border-zinc-200 bg-zinc-50 hover:bg-zinc-100 cursor-pointer transition-all hover:scale-[1.01]"
                  >
                    <div className="flex items-center justify-between">
                      <p className="font-medium text-zinc-900 text-sm">&#10148; {tecnico.nombre_tecnico}</p>
                      {vacacion && (
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                          EVENTOS_INTERNOS[normalizarTipoEvento(vacacion.tipo_asueto || "Vacaciones")]?.badge || "bg-zinc-100 text-zinc-700"
                        }`}>
                          {normalizarTipoEvento(vacacion.tipo_asueto || "Vacaciones")}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-zinc-600 mt-0.5">
                      {vacacion
                        ? `Último: ${formatFecha(vacacion.fecha_inicio)} → ${formatFecha(vacacion.fecha_fin)}`
                        : "Sin registros"}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
          </div>
        </div>
      </div>

      {/* ====================== MODAL: Batch assignment ====================== */}
      {modalLoteOpen && (
        <>
          <div className="overlay" onClick={() => !isSubmitting && setModalLoteOpen(false)} />
          <div className="modal max-w-lg w-full border border-zinc-200 rounded-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-zinc-900">Asignar registros por lote</h2>
              <button className="btn-icon" onClick={() => !isSubmitting && setModalLoteOpen(false)}>
                <HiXMark className="h-5 w-5" />
              </button>
            </div>
            <p className="text-sm text-zinc-600 mb-4">Selecciona 2 o más personas para asignar vacaciones, trabajo interno o día feriado</p>

            <fieldset disabled={isSubmitting} className="space-y-4">
              <div>
                <label className="label">Tipo de registro</label>
                <select className="input w-full border-zinc-300" value={selectedTipoAsueto} onChange={e => setSelectedTipoAsueto(e.target.value)}>
                  <option value="">Seleccionar tipo</option>
                  {Object.keys(EVENTOS_INTERNOS).map(tipo => <option key={tipo} value={tipo}>{tipo}</option>)}
                </select>
              </div>
              <div>
                <label className="label">Comentarios adicionales (opcional)</label>
                <textarea className="input w-full border-zinc-300" rows={2} maxLength={1000} value={comentarios} onChange={e => setComentarios(e.target.value)} />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="label mb-0">Técnicos para el registro</label>
                  <button className="text-xs text-zinc-700 hover:underline" onClick={handleToggleSelectAll}>
                    {selectAll ? "Deseleccionar todos" : "Seleccionar todos"}
                  </button>
                </div>
                <div className="input max-h-40 overflow-y-auto p-2 space-y-1 border-zinc-300">
                  {tecnicos.map((t) => (
                    <label key={t.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-zinc-50 rounded px-1 py-0.5">
                      <input
                        type="checkbox"
                        checked={selectedTecnicos.includes(t.id)}
                        onChange={() => handleChangeMultiple(t.id)}
                        className="rounded border-gray-300 text-slate-900 focus:ring-slate-500"
                      />
                      <span className="truncate">{t.nombre_tecnico}</span>
                    </label>
                  ))}
                </div>
                {selectedTecnicos.length > 0 && (
                  <p className="text-xs text-zinc-500 mt-1">{selectedTecnicos.length} seleccionados</p>
                )}
              </div>

              <div className="flex gap-3">
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
                  <div className="flex-1">
                    <DatePicker
                      label="Fecha inicio"
                      value={fechaInicio}
                      format="dd/MM/yyyy"
                      onChange={(value) => setFechaInicio(value)}
                      slotProps={{ textField: { size: "small", fullWidth: true } }}
                    />
                  </div>
                  <div className="flex-1">
                    <DatePicker
                      label="Fecha fin"
                      value={fechaFin}
                      format="dd/MM/yyyy"
                      onChange={(value) => setFechaFin(value)}
                      slotProps={{ textField: { size: "small", fullWidth: true } }}
                    />
                  </div>
                </LocalizationProvider>
              </div>

              <button
                className="btn-primary w-full flex items-center justify-center gap-2 bg-zinc-900 hover:bg-black"
                onClick={() => guardarEventos(true)}
                disabled={isSubmitting}
              >
                <HiExclamationTriangle className="h-4 w-4" />
                {isSubmitting ? "Registrando..." : "Agregar registros por lote"}
              </button>
            </fieldset>
          </div>
        </>
      )}

      {/* ====================== MODAL: Vacation history ====================== */}
      {modalVacacionesOpen && (
        <>
          <div className="overlay" onClick={() => { setModalVacacionesOpen(false); setVacacionesModal([]); }} />
          <div className="modal max-w-lg w-full border border-zinc-200 rounded-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-zinc-900">
                Registros de: {selectedTecnicoModal?.nombre_tecnico}
              </h2>
              <button className="btn-icon" onClick={() => { setModalVacacionesOpen(false); setVacacionesModal([]); }}>
                <HiXMark className="h-5 w-5" />
              </button>
            </div>

            <div className="card bg-zinc-50 border-zinc-200 max-h-60 overflow-y-auto p-4">
              <p className="text-sm font-medium text-zinc-700 mb-2">Registros (vacaciones, trabajo interno y día feriado):</p>
              {vacacionesModal.length === 0 ? (
                <p className="text-sm text-zinc-400">Sin registros.</p>
              ) : (
                <div className="divide-y divide-zinc-200">
                  {vacacionesModal.map((vacacion, index) => (
                    <div key={index} className="flex items-center justify-between py-2 gap-2">
                      <div className="flex flex-col min-w-0">
                        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full self-start mb-0.5 ${
                          EVENTOS_INTERNOS[normalizarTipoEvento(vacacion.tipo_asueto || "Vacaciones")]?.badge || "bg-zinc-100 text-zinc-700"
                        }`}>
                          {normalizarTipoEvento(vacacion.tipo_asueto || "Vacaciones")}
                        </span>
                        <span className="text-sm text-zinc-700">
                          {formatFecha(vacacion.fecha_inicio)} → {formatFecha(vacacion.fecha_fin)}
                        </span>
                        {vacacion.comentarios && vacacion.comentarios !== "Sin comentarios" && (
                          <span className="text-xs text-zinc-400 truncate">{vacacion.comentarios}</span>
                        )}
                      </div>
                      <button className="btn-icon text-red-500 hover:bg-red-50 flex-shrink-0" onClick={() => handleDeleteVacaciones(vacacion.id, vacacion.id_reserva)}>
                        <HiTrash className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-end mt-4">
              <button className="btn-danger bg-zinc-900 hover:bg-black" onClick={() => { setModalVacacionesOpen(false); setVacacionesModal([]); }}>
                Cerrar
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PanelControlInfo;
